import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Chip, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell from "../Common/EllipsisedCell";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import UploadFileCommonDialog, { downloadExcelFromBuffer } from "./UploadFileCommonDialog";
import UploadInvoiceFilters from "./UploadInvoiceFilters";

export function filterDateBetweenStartAndEndDate(start: string | Date, end: string | Date, current: string | Date) {
  const startDate = start && moment(start).format("YYYY-MM-DD");
  const endDate = end && moment(end).format("YYYY-MM-DD");
  const currentDate = current && moment(current).format("YYYY-MM-DD");
  if (!startDate && !endDate && !currentDate) return true;
  if (!currentDate) return false;
  if (startDate && endDate) return currentDate >= startDate && currentDate <= endDate;
  if (startDate) return currentDate >= startDate;
  if (endDate) return currentDate <= endDate;
  return true;
}
export const datefilterFn = (value, filterValue) => {
  const startDate = filterValue[0] ? dayjs(filterValue[0]).toDate() : null;
  const endDate = filterValue[1] ? dayjs(filterValue[1]).toDate() : null;
  if (startDate || endDate) {
    return filterDateBetweenStartAndEndDate(startDate, endDate, value);
  }
};

export type AllowedFilterKeysUnpaidInvoices = "vendorName" | "panNumber" | "grnNumber" | "invoiceNumber";
export interface UnpaidInvoiceResponseDto {
  id: string;
  entityName: string;
  vendorCode: string;
  vendorName: string;
  pan: string;
  gstin: string;
  warehouseName: string;
  city: string;
  state: string;
  invoicePostedStatus: string;
  poNumber: string;
  poDate: Date | null;
  poAmount: number;
  grnNumber: string;
  grnDate: Date | null;
  grnAmount: number;
  creatorInvoiceDate: Date | null;
  invoiceNumber: string;
  invoiceAccountDate: Date | null;
  invoiceAmount: number;
  tds: number;
  purchaseReturnAmount: number;
  brandDiscount: number;
  debitAmount: number;
  adjustments: number;
  netPayableAmount: number;
  otherAdjustmentDate: Date | null;
  invoiceReconciledStatus: string;
  paymentAmount: number;
  paymentReferenceNumber: string;
  outstandingAmount: number;
  creditPeriod: number;
  dueDate: Date | null;
  newDueDate: Date | null;
  overDueStatus: string;
  paymentStatus: string;
  lastPaymentDate: Date | null;
  isfreezed: boolean;
}

export default function UploadInvoice() {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();

  const [openFilters, setOpenFilters] = React.useState<boolean>(false);
  const [openUploadInvoiceModal, setOpenUploadInvoiceModal] = React.useState<boolean>(false);
  const [invoiceData, setInvoiceData] = React.useState<UnpaidInvoiceResponseDto[]>([]);
  const lastRefreshedAt = React.useRef<Date | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<AllowedFilterKeysUnpaidInvoices, string[]>>({
    vendorName: [],
    panNumber: [],
    grnNumber: [],
    invoiceNumber: [],
  });
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);

  useEffect(() => {
    getAllInvoicesData();
  }, [appliedFilters]);

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);
    await useFetch("/unpaid-invoices/report", "POST", {
      data: {
        vendorName: appliedFilters.vendorName || [],
        panNumber: appliedFilters.panNumber || [],
        grnNumber: appliedFilters.grnNumber || [],
        invoiceNumber: appliedFilters.invoiceNumber || [],
        anchorId: 2,
      },
      failureMessage: "Failed to download report",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.unpaidInvoicesReport, "unpaid-invoices-report.xlsx");
        setIsDownloadingReport(false);
      },
    });
  };

  const getAllInvoicesData = async () => {
    setIsLoading(true);
    await useFetch("/unpaid-invoices", "POST", {
      failureMessage: "Failed to fetch unpaid invoices",
      data: {
        vendorName: appliedFilters.vendorName || [],
        panNumber: appliedFilters.panNumber || [],
        grnNumber: appliedFilters.grnNumber || [],
        invoiceNumber: appliedFilters.invoiceNumber || [],
        anchorId: 2,
      },
      thenCallBack: (response) => {
        setInvoiceData((response.data.result.invoices as UnpaidInvoiceResponseDto[]) || []);
        lastRefreshedAt.current = response.data.result.lastRefreshedAt;
        setIsLoading(false);
      },
      catchCallBack: () => {
        setIsLoading(false);
      },
    });
  };

  const columns = useMemo<MRT_ColumnDef<UnpaidInvoiceResponseDto>[]>(
    () => [
      {
        accessorKey: "entityName",
        header: "Organization Name",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "vendorName",
        header: "Vendor Name",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "vendorCode",
        header: "Vendor Code",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "gstin",
        header: "GSTIN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "pan",
        header: "PAN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "warehouseName",
        header: "Warehouse Name",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "city",
        header: "City",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "state",
        header: "State",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "invoicePostedStatus",
        header: "Status of Invoice",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "poNumber",
        header: "PO No.",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "poDate",
        header: "PO Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.poDate, filterValue);
        },
        accessorFn: (row) => (row.poDate ? new Date(row.poDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
      {
        accessorKey: "poAmount",
        header: "PO Amount",
        filterVariant: "range",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "grnNumber",
        header: "GRN Number",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "grnDate",
        header: "GRN Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.grnDate, filterValue);
        },
        accessorFn: (row) => (row.grnDate ? new Date(row.grnDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
      {
        accessorKey: "creatorInvoiceDate",
        header: "Creator Invoice Date",
        size: 250,
        accessorFn: (row) => (row.creatorInvoiceDate ? moment(row.creatorInvoiceDate).format("DD/MM/YYYY") : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "grnAmount",
        header: "GRN Amount",
        filterVariant: "range",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "invoiceNumber",
        header: "Invoice Number",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "invoiceAccountDate",
        header: "Invoice Accounting Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceAccountDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceAccountDate ? new Date(row.invoiceAccountDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
      {
        accessorKey: "invoiceAmount",
        header: "Invoice amount",
        filterVariant: "range",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "tds",
        header: "TDS/TCS",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="number" />,
      },
      {
        accessorKey: "purchaseReturnAmount",
        header: "Purchase Return Amount",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "brandDiscount",
        header: "Brand discount (Promo Claims)",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "debitAmount",
        header: "Other Debit Amount",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "adjustments",
        header: "Other adjustments",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "netPayableAmount",
        header: "Net Payable Amount",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "otherAdjustmentDate",
        header: "Other Adjustment Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.otherAdjustmentDate, filterValue);
        },
        accessorFn: (row) => (row.otherAdjustmentDate ? new Date(row.otherAdjustmentDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
      {
        accessorKey: "invoiceReconciledStatus",
        header: "Invoice Status",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "paymentAmount",
        header: "Payment amount",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "paymentReferenceNumber",
        header: "Payment Reference No",
        size: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "outstandingAmount",
        header: "Outstanding payment",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        accessorKey: "creditPeriod",
        header: "Credit Period(In Days)",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="number" />,
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.dueDate, filterValue);
        },
        accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
      {
        accessorKey: "overDueStatus",
        header: "Overdue",
        size: 250,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment Status",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "lastPaymentDate",
        header: "Last Payment Date",
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.lastPaymentDate, filterValue);
        },
        accessorFn: (row) => (row.lastPaymentDate ? new Date(row.lastPaymentDate) : null),
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<Date>()} type="date" />,
      },
    ],
    []
  );
  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          p: 2,
          my: 2,
          backgroundColor: "#fff",
          borderRadius: 1,
          border: "1px solid #E5E5E5",
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Upload Unpaid Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload the daily unpaid invoices file to update the system with latest invoice data. The file should contain
            GRN numbers, invoice details, and outstanding amounts.
          </Typography>
        </Box>
        <CustomButton variant="contained" onClick={() => setOpenUploadInvoiceModal(true)} sx={{ flexShrink: 0 }}>
          Upload file
        </CustomButton>
      </Box>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            direction: "row",
            justifyContent: "flex-start",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Unpaid Invoices Tracker
          </Typography>
          <Chip
            label={
              lastRefreshedAt.current
                ? `Last refreshed at ${moment(lastRefreshedAt.current).format("DD/MM/YY h:mm A")} IST`
                : "Not refreshed yet"
            }
            sx={{
              color: "#0288D1",
              backgroundColor: "#E3F2FD",
              borderRadius: "4px",
              "& .MuiChip-label": { fontSize: "14px", fontWeight: 600 },
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <CustomButton onClick={() => setOpenFilters(true)} variant="outlined" startIcon={<FilterListIcon />}>
            Filter
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={handleDownloadReport}
            disabled={isDownloadingReport}
            startIcon={isDownloadingReport ? <CircularProgress size={20} /> : null}
          >
            Download Report
          </CustomButton>
        </Box>
      </Paper>
      <Divider />
      <ReactTable
        data={invoiceData}
        columns={columns}
        isLoading={isLoading}
        enableColumnOrdering={true}
        enableRowVirtualization={true}
        enableColumnVirtualization={true}
        additionalInitialState={{
          columnVisibility: {
            city: false,
            state: false,
            invoicePostedStatus: false,
            poNumber: false,
            poDate: false,
            poAmount: false,
            tds: false,
            purchaseReturnAmount: false,
            brandDiscount: false,
            debitAmount: false,
            adjustments: false,
            otherAdjustmentDate: false,
            invoiceReconciledStatus: false,
            creditPeriod: false,
            overDueStatus: false,
            lastPaymentDate: false,
          },
        }}
      />
      {openFilters && (
        <UploadInvoiceFilters
          open={openFilters}
          onClose={() => {
            setOpenFilters(false);
          }}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
        />
      )}
      {openUploadInvoiceModal && (
        <UploadFileCommonDialog
          open={openUploadInvoiceModal}
          onClose={() => setOpenUploadInvoiceModal(false)}
          useCase="uploadUnpaidInvoices"
          getAllData={getAllInvoicesData}
        />
      )}
    </LoggedInSkeleton>
  );
}
